import store from '@/store';
import { getPlatformInfo } from '@/utils/deviceUtils';
import _ from 'lodash';
import DDLogs from '@/DDLogs';
import Segment from '@/Segment';
import config from './config';

// True when show() was called from a user-initiated path. Consumed in onShow
// so init-time Intercom('show') calls in initIntercomEvents don't count as opens.
let userInitiatedShowPending = false;

// True once the messenger iframe's own scripts have actually executed and called
// back into the parent (onUnreadCountChange fires on init). window.Intercom.booted
// only means the iframe element + script tags were created — not that the scripts
// inside it ran. This flag is the real "iframe is alive" signal.
let iframeMessengerAlive = false;

let showRetryTimer = null;
let showRebootTimer = null;
let lastBootArgs = null;
let lastUpdateArgs = null;
const SHOW_RETRY_DELAY_MS = 1000;
const SHOW_REBOOT_DELAY_MS = 1000;

/* eslint-disable */
export default {
  install(Vue, options) {
    includeIntercom();
  },

  async boot(customerId, householdName, extraAttributes) {
    const { intercomEnabled, appId, normalizedExtraAttributes } = _getIntercomAttributes(extraAttributes);
    if (intercomEnabled === "true") {
      if (!_isIntercomAvailable()) {
        DDLogs.error('[Intercom Debug] Intercom not available on window during boot');
        return;
      }
      DDLogs.info('[Intercom Debug] Booting Intercom', { customerId });
      lastBootArgs = {
        app_id: appId,
        user_id: customerId,
        name: householdName,
        hide_default_launcher: true,
        ...normalizedExtraAttributes,
      };
      window.Intercom('boot', lastBootArgs);

      this.initIntercomEvents();
    }
  },
  show() {
    if (_isIntercomAvailable()) {
      DDLogs.info('[Intercom Debug] show clicked and Intercom available on window', {
        iframeExists: document.getElementById('intercom-frame') !== null,
        widgetBooted: !!window.Intercom.booted,
        iframeAlive: iframeMessengerAlive,
        intercomIsOpen: !!store.state.intercom.isOpen,
      });
      userInitiatedShowPending = true;
      window.Intercom('show');
      const self = this;
      if (!showRetryTimer) {
        showRetryTimer = setTimeout(function() {
          showRetryTimer = null;
          if (!userInitiatedShowPending) return;
          DDLogs.info('[Intercom Debug] show retry fired', {
            iframeAlive: iframeMessengerAlive,
            widgetBooted: !!window.Intercom.booted,
          });
          window.Intercom('show');
          if (!showRebootTimer) {
            showRebootTimer = setTimeout(function() {
              showRebootTimer = null;
              if (!userInitiatedShowPending || !lastBootArgs) return;
              const currentCustomerId = store.state.session && store.state.session.customerId;
              if (String(lastBootArgs.user_id) !== String(currentCustomerId)) {
                DDLogs.info('[Intercom Debug] reboot aborted — cached boot args are stale for current session', {
                  cachedUserId: lastBootArgs.user_id,
                  currentCustomerId,
                });
                return;
              }
              DDLogs.info('[Intercom Debug] show reboot triggered — retry also failed', {
                iframeAlive: iframeMessengerAlive,
              });
              iframeMessengerAlive = false;
              window.Intercom('shutdown');
              window.Intercom('boot', lastBootArgs);
              self.initIntercomEvents();
              if (lastUpdateArgs) {
                window.Intercom('update', lastUpdateArgs);
              }
              window.Intercom('show');
            }, SHOW_REBOOT_DELAY_MS);
          }
        }, SHOW_RETRY_DELAY_MS);
      }
    } else {
      DDLogs.error('[Intercom Debug] show attempted but Intercom not available on window');
    }
  },
  hide() {
    if (_isIntercomAvailable()) {
      window.Intercom('hide');
    } else {
      DDLogs.info('[Intercom Debug] hide attempted but Intercom not available');
    }
  },
  async update(customerId, householdName, extraAttributes) {
    const { intercomEnabled, appId, normalizedExtraAttributes } = _getIntercomAttributes(extraAttributes);
    if (intercomEnabled === "true") {
      if (!_isIntercomAvailable()) {
        DDLogs.error('[Intercom Debug] Intercom not available on window during update');
        return;
      }
      lastUpdateArgs = {
        app_id: appId,
        user_id: customerId,
        name: householdName,
        hide_default_launcher: true,
        ...normalizedExtraAttributes,
      };
      window.Intercom('update', lastUpdateArgs);
    }
  },
  hideLauncher() {
    DDLogs.info('[Intercom Debug] hideLauncher called', { isAvailable: _isIntercomAvailable() });
    window.Intercom('update', {
      'hide_default_launcher': true
    });
  },
  showLauncher() {
    DDLogs.info('[Intercom Debug] showLauncher called (no-op)');
  },
  showNewMessage(prompt = '') {
    if (_isIntercomAvailable()) {
      DDLogs.info('[Intercom Debug] showNewMessage called');
      window.Intercom('showNewMessage', prompt);
    } else {
      DDLogs.info('[Intercom Debug] showNewMessage attempted but Intercom not available');
    }
  },
  showConversation(conversationId) {
    if (_isIntercomAvailable()) {
      DDLogs.info('[Intercom Debug] showConversation called', { conversationId });
      userInitiatedShowPending = true;
      window.Intercom('showConversation', conversationId);
    } else {
      DDLogs.info('[Intercom Debug] showConversation attempted but Intercom not available');
    }
  },
  initIntercomEvents() {
    if (!window.Intercom) {
      DDLogs.info('[Intercom Debug] initIntercomEvents skipped — Intercom not on window');
      return;
    }
    DDLogs.info('[Intercom Debug] initIntercomEvents started');

    const { platform, appVersion } = getPlatformInfo();
    window.Intercom('onShow', function() {
      if (userInitiatedShowPending) {
        userInitiatedShowPending = false;
        if (showRetryTimer) {
          clearTimeout(showRetryTimer);
          showRetryTimer = null;
        }
        if (showRebootTimer) {
          clearTimeout(showRebootTimer);
          showRebootTimer = null;
        }
        DDLogs.info('[Intercom Debug] onShow fired — messenger actually opened');
        Segment.trackUserInteraction('intercom_chat_opened');
      }
      window.Intercom('update', { user_platform: platform, appVersion: appVersion });
      store.commit('intercom/setIsOpen', true);
      store.commit('intercom/setHasOpenedIntercom', true);
    });

    window.Intercom('onHide', function() {
      store.commit('intercom/setIsOpen', false);
    });

    if (store.state.intercom.showOnInit) {
      window.Intercom('show');
    }

    window.Intercom('onUnreadCountChange', (unreadCount) => {
      iframeMessengerAlive = true;
      store.commit('intercom/setUnreadMessages', unreadCount);
    });
  }
};
function includeIntercom() {
  const w = window;
  const ic = w.Intercom;
  if (typeof ic === 'function') {
    ic('reattach_activator');
    ic('update', w.intercomSettings);
  } else {
    const d = document;
    const i = function() {
      i.c(arguments);
    };
    i.q = [];
    i.c = function(args) {
      i.q.push(args);
    };
    w.Intercom = i;
    const l = function() {
      const s = d.createElement('script');
      s.type = 'text/javascript';
      s.async = true;
      s.src = 'https://widget.intercom.io/widget/APP_ID';
      const x = d.getElementsByTagName('script')[0];
      x.parentNode.insertBefore(s, x);
    };
    // includeIntercom runs at Vue plugin install, when readyState is usually
    // 'interactive' — waiting for the 'load' event injects the script too late
    // (or never, if the session ends first), so run as soon as the DOM is ready.
    if (document.readyState === 'complete' || document.readyState === 'interactive') {
      l();
    } else {
      w.addEventListener('DOMContentLoaded', l, false);
    }
  }
}

function _isIntercomAvailable() {
  return window.Intercom && typeof window.Intercom === 'function';
}

function _getIntercomAttributes(extraAttributes) {
  const { intercomEnabled, intercomProdAppId } = config.get();
  const normalizedExtraAttributes = _.mapKeys(extraAttributes, (v, k) => _.snakeCase(k));
  return { intercomEnabled, appId: intercomProdAppId, normalizedExtraAttributes };
}
